import { useDebouncedState } from "@react-hookz/web";
import { useEffect, useMemo, useState } from "react";
import { Autocomplete, Button, Icon, IconButton, Spinner, Tooltip, Typography } from "@/components/atoms";
import { EmptyState } from "@/components/molecules";
import { useOperator } from "@/hooks";
import { useLazySearchDriverQuery } from "@/redux/apis/typeahead/typeaheadApi";
import { clsx, getAssetUrl } from "@/utils";
import { RemoveDriverModal } from "../../AddVehicle/modals/RemoveDriverModal";
import { V3AccessDraft, V3Driver } from "../steps";

/**
 * Step 4 (DEV-1903): who may drive the vehicle — the plate path's access level
 * and driver search, held as draft data: the access is granted in the same
 * request that creates the vehicle, at Review.
 */
export const DriversStep = ({
  saved,
  onBack,
  onNext,
  onFinishLater,
  saving,
}: {
  saved: V3AccessDraft | undefined;
  onBack: () => void;
  onNext: (access: V3AccessDraft) => void;
  onFinishLater: (access: V3AccessDraft) => void;
  saving: boolean;
}) => {
  const operator = useOperator();
  const [level, setLevel] = useState<V3AccessDraft["level"]>(saved?.level ?? "RESTRICTED");
  const [drivers, setDrivers] = useState<V3Driver[]>(saved?.drivers ?? []);
  const [pendingRemove, setPendingRemove] = useState<V3Driver | null>(null);
  const [query, setQuery] = useDebouncedState("", 300);
  const [searchDriver, { isFetching, data }] = useLazySearchDriverQuery();
  const [value, setValue] = useState<V3Driver | null>(null);

  useEffect(() => {
    if (query.length > 1) searchDriver({ q: query });
  }, [query, searchDriver]);

  const options = useMemo(
    () => data?.map((i) => ({ id: i.id, name: i.name, avatar: i.avatar, email: i.email })) as V3Driver[] | undefined,
    [data]
  );

  const access = (): V3AccessDraft => ({ level, drivers: level === "RESTRICTED" ? drivers : [] });

  const select = (option: V3Driver | null) => {
    setValue(option);
    if (!option || drivers.some((d) => d.id === option.id)) return;
    setDrivers((previous) => [...previous, option]);
    setValue(null);
  };

  return (
    <>
      <div className="flex flex-col gap-6">
        <div>
          <Typography variant="action">Select Access Level</Typography>
          <div className="mt-3 grid grid-cols-2 gap-3">
            <LevelButton active={level === "RESTRICTED"} title="Restricted" onClick={() => setLevel("RESTRICTED")}>
              Only drivers with access can use this vehicle
            </LevelButton>
            <LevelButton active={level === "ALL"} title="All Drivers" onClick={() => setLevel("ALL")}>
              All drivers within {operator.name} can use this vehicle
            </LevelButton>
          </div>
        </div>

        {level === "RESTRICTED" ? (
          <>
            <div>
              <Typography variant="action" className="text-neutral-dark-gray">
                Enter the driver&apos;s email to share this vehicle for bookings
              </Typography>
              <Autocomplete
                options={options}
                isLoading={isFetching}
                value={value}
                onValueChange={select}
                onQueryChange={setQuery}
                className="mt-2"
                placeholder="Search and add driver"
              >
                <Autocomplete.Options className="mt-2 max-h-72 w-full overflow-auto rounded-md border border-neutral-mid-gray bg-white text-base shadow-lg empty:mt-0">
                  {options?.map((option) => {
                    const added = drivers.some((d) => d.id === option.id);
                    return (
                      <Autocomplete.Option
                        key={option.id}
                        value={option}
                        className={clsx("flex w-full cursor-pointer px-4 pb-3 pt-4 hover:bg-primary-light", added && "pointer-events-none")}
                      >
                        <div className="flex-1">
                          <Typography className={added ? "text-neutral-dark-gray" : "text-neutral-black"}>{option.name}</Typography>
                          <Typography variant="small" className="text-neutral-dark-gray">
                            {option.email}
                          </Typography>
                        </div>
                        {added && <Icon name="TickCircle" variant="Bold" className="text-success" />}
                      </Autocomplete.Option>
                    );
                  })}
                </Autocomplete.Options>
              </Autocomplete>
            </div>

            <div>
              <Typography variant="action">Drivers with Access ({drivers.length})</Typography>
              <div className="mt-3 flex flex-col gap-2.5">
                {drivers.length ? (
                  drivers.map((driver) => (
                    <div key={driver.id} className="flex items-center gap-3 rounded-lg bg-neutral-surface-gray p-3">
                      <img
                        src={getAssetUrl(driver.avatar, { height: 80, width: 80, background: "transparent" })}
                        className="h-10 w-10 rounded-full object-cover"
                        alt=""
                      />
                      <div className="flex flex-1 flex-col">
                        <Typography className="text-neutral-black">{driver.name}</Typography>
                        <Typography variant="small" className="text-neutral-dark-gray">
                          {driver.email}
                        </Typography>
                      </div>
                      <Tooltip content="Remove this driver's access to this vehicle" placement="left">
                        <IconButton
                          iconName="close"
                          isCustomIcon
                          variant="custom"
                          iconSize="lg"
                          className="text-neutral-dark-gray"
                          aria-label={`Remove ${driver.name}`}
                          onClick={() => setPendingRemove(driver)}
                        />
                      </Tooltip>
                    </div>
                  ))
                ) : (
                  <EmptyState
                    title="Nothing here... yet"
                    description="Search and add a driver to share this vehicle to"
                    hideImage
                    className="rounded-lg border border-neutral-mid-gray bg-neutral-surface-gray"
                  />
                )}
              </div>
            </div>
          </>
        ) : (
          <EmptyState
            title={`All drivers within ${operator.name} will have access to this vehicle`}
            description="You can manage individual driver access after the vehicle has been added."
            hideImage
            className="rounded-lg bg-neutral-surface-gray"
          />
        )}

        <div className="mt-4 flex flex-col-reverse gap-3 xl:flex-row xl:justify-between xl:gap-4">
          <Button
            type="button"
            variant="custom"
            size="xs"
            className="text-neutral-silver-gray underline xl:w-auto"
            disabled={saving}
            onClick={() => onFinishLater(access())}
          >
            {saving ? <Spinner /> : "Finish later"}
          </Button>
          <div className="flex flex-col-reverse gap-3 xl:flex-row xl:gap-4">
            <Button type="button" variant="secondary" size="lg" className="w-full xl:w-auto" startIcon="ArrowLeft" onClick={onBack}>
              Back
            </Button>
            <Button type="button" variant="primary" size="lg" className="w-full xl:w-auto" endIcon="ArrowRight" onClick={() => onNext(access())}>
              Next
            </Button>
          </div>
        </div>
      </div>

      <RemoveDriverModal
        open={!!pendingRemove}
        driverName={pendingRemove?.name ?? ""}
        onClose={() => setPendingRemove(null)}
        onRemove={() => {
          setDrivers((previous) => previous.filter((d) => d.id !== pendingRemove?.id));
          setPendingRemove(null);
        }}
      />
    </>
  );
};

const LevelButton = ({ active, title, onClick, children }: { active: boolean; title: string; onClick: () => void; children: React.ReactNode }) => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={active}
    className={clsx(
      "rounded-lg border-2 p-4 text-center transition-colors",
      active ? "border-primary bg-primary-light" : "border-neutral-mid-gray bg-neutral-surface-gray"
    )}
  >
    <Typography variant="action" className={active ? "text-primary" : undefined}>
      {title}
    </Typography>
    <Typography variant="paragraph" className="mt-1 text-neutral-dark-gray">
      {children}
    </Typography>
  </button>
);
